import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Globe2, LogIn, Map, MountainSnow } from "lucide-react";
import { fetchRegions, type RegionListItem, type RegionsListResponse } from "../api/regions";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { AtlasPanel } from "../components/AtlasPanel";
import { ProgressRing } from "../components/ProgressRing";
import { Badge } from "../components/Badge";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";

const COUNTRY_LABELS: Record<string, string> = {
  GB: "United Kingdom",
  US: "United States",
  FR: "France",
  DE: "Germany",
  NO: "Norway",
  AU: "Australia",
  NZ: "New Zealand",
};

function countryLabel(code: string | null) {
  if (!code) return null;
  return COUNTRY_LABELS[code] ?? code;
}

// ATL-47: headline percentage is item completion; prerendered static data
// from before that field shipped falls back to area coverage.
function regionPct(region: RegionListItem): number {
  return region.completionPct ?? region.coveragePct;
}

function regionStarted(region: RegionListItem): boolean {
  return regionPct(region) > 0 || region.coveragePct > 0;
}

function RegionCard({ region, onClick }: { region: RegionListItem; onClick: () => void }) {
  const pct = regionPct(region);
  const started = regionStarted(region);
  const hasImage = Boolean(region.heroImage);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        width: "100%",
        textAlign: "left",
        background: hasImage ? "var(--surface-sunken)" : "var(--surface-card)",
        backgroundImage: hasImage
          ? `linear-gradient(180deg, rgba(9,12,16,0.25) 0%, rgba(9,12,16,0.85) 100%), url("${region.heroImage}")`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundOrigin: "border-box",
        border: `1px solid ${started ? "var(--border-gold)" : "var(--border-subtle)"}`,
        borderRadius: "var(--radius-lg)",
        padding: "16px 20px",
        cursor: "pointer",
        boxShadow: started ? "var(--glow-gold-sm), var(--ring-top)" : "var(--ring-top)",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        font: "inherit",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <ProgressRing value={pct} size={64} stroke={7} label={null} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 17,
          color: hasImage ? "#fff" : "var(--text-primary)",
          textShadow: hasImage ? "0 1px 3px rgba(0,0,0,.5)" : undefined,
          letterSpacing: "-.01em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          marginBottom: 5,
        }}>
          {region.name}
        </div>

        {countryLabel(region.country) && (
          <div
            className={hasImage ? undefined : "eyebrow"}
            style={{
              fontSize: 10.5,
              marginBottom: 8,
              color: hasImage ? "rgba(255,255,255,.78)" : undefined,
              textTransform: hasImage ? "uppercase" : undefined,
              letterSpacing: hasImage ? ".08em" : undefined,
              fontFamily: hasImage ? "var(--font-mono)" : undefined,
            }}
          >
            {countryLabel(region.country)}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {pct >= 100 ? (
            <Badge variant="gold" dot>Complete</Badge>
          ) : started ? (
            <Badge variant="success">
              {region.itemsTotal != null && region.itemsTotal > 0
                ? `${region.itemsCollected ?? 0} / ${region.itemsTotal} collected`
                : `${Math.round(pct)}% complete`}
            </Badge>
          ) : (
            <Badge variant="neutral">Not started</Badge>
          )}
        </div>
      </div>

      <div style={{ color: hasImage ? "rgba(255,255,255,.78)" : "var(--text-faint)", flexShrink: 0 }}>
        <Map size={18} />
      </div>
    </button>
  );
}

interface RegionsPageProps {
  staticData?: RegionsListResponse;
}

export default function RegionsPage({ staticData }: RegionsPageProps = {}) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isAuthenticated = !!me;

  const { data, isLoading } = useQuery({
    queryKey: ["regions"],
    queryFn: fetchRegions,
    enabled: !staticData,
    initialData: staticData,
  });

  const regions = data?.regions ?? [];
  const started = regions.filter(regionStarted);
  const complete = regions.filter((r) => regionPct(r) >= 100);
  const countries = [...new Set(regions.map((r) => r.country).filter(Boolean))];
  const avgPct =
    started.length > 0
      ? Math.round(started.reduce((s, r) => s + regionPct(r), 0) / started.length)
      : 0;

  usePageMeta({
    title: "Regions | Atlas",
    description:
      regions.length > 0
        ? `${regions.length} explorable regions worldwide — track your coverage across peaks, trails and landmarks region by region.`
        : "Explore Atlas's regions worldwide — track your coverage across peaks, trails and landmarks region by region.",
    canonicalPath: "/regions",
  });

  return (
    <div className="r-page" style={{ padding: 28, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
      {/* hero row */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 20, flexShrink: 0 }}>
        {/* left: summary */}
        <div
          className="atlas-topo"
          style={{
            position: "relative",
            borderRadius: "var(--radius-xl)",
            border: "1px solid var(--border-gold)",
            padding: 28,
            overflow: "hidden",
            boxShadow: "var(--glow-gold-sm), var(--ring-top)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 26,
          }}
        >
          {isLoading ? (
            <div style={{ width: 124, height: 124, flexShrink: 0 }}><Skel width="124px" height="124px" /></div>
          ) : (
            <ProgressRing value={avgPct} size={124} stroke={12} label="Avg complete" />
          )}

          <div className="r-basis-full" style={{ flex: 1, minWidth: 220 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Regions · Worldwide</div>

            {isLoading ? (
              <Skel height="46px" width="160px" />
            ) : (
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
                <span
                  className="atlas-gold-text"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: 46,
                    lineHeight: 1,
                    letterSpacing: "-.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {started.length}
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                  / {regions.length} regions explored
                </span>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
              <Badge variant="gold" dot>{regions.length} regions tracked</Badge>
              {complete.length > 0 && <Badge variant="success">{complete.length} complete</Badge>}
              {countries.length > 0 && (
                <Badge variant="neutral">
                  <Globe2 size={10} style={{ display: "inline", verticalAlign: "middle", marginRight: 3 }} />
                  {countries.length} {countries.length === 1 ? "country" : "countries"}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* right: leaderboard / top regions */}
        <AtlasPanel
          title="Most complete"
          action={started.length > 0 ? <Badge variant="gold">{started.length} active</Badge> : undefined}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {isLoading ? (
              [0, 1, 2].map((i) => <Skel key={i} height="44px" />)
            ) : !isAuthenticated ? (
              <EmptyState
                icon={<LogIn size={18} />}
                title="Sign up to track your regions"
                hint="Create a free account to log coverage as you explore — Strava, GPX and more."
                action={{ label: "Sign up", onClick: () => void navigate({ to: "/signup" }) }}
              />
            ) : started.length === 0 ? (
              <EmptyState
                icon={<Map size={18} />}
                title="No regions started yet"
                hint="Connect Strava or upload a GPX to get going."
              />
            ) : (
              [...started]
                .sort((a, b) => regionPct(b) - regionPct(a))
                .slice(0, 4)
                .map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => void navigate({ to: "/regions/$slug", params: { slug: r.slug } })}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    <span style={{
                      width: 38, height: 38, flex: "0 0 auto",
                      borderRadius: "var(--radius-md)",
                      display: "grid", placeItems: "center",
                      background: "var(--accent-soft)",
                      border: "1px solid var(--border-gold)",
                      color: "var(--gold-400)",
                    }}>
                      <MountainSnow size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "baseline",
                        gap: 8, marginBottom: 4,
                      }}>
                        <span style={{
                          fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {r.name}
                        </span>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", flex: "0 0 auto" }}>
                          {Math.round(regionPct(r))}%
                        </span>
                      </div>
                      <div style={{
                        height: 4, borderRadius: 4,
                        background: "var(--surface-sunken)",
                        overflow: "hidden",
                      }}>
                        <div style={{
                          height: "100%",
                          width: `${Math.min(regionPct(r), 100)}%`,
                          borderRadius: 4,
                          background: "var(--accent)",
                          boxShadow: "var(--glow-gold-sm)",
                          transition: "width 0.4s ease",
                        }} />
                      </div>
                    </div>
                  </button>
                ))
            )}
          </div>
        </AtlasPanel>
      </div>

      {/* all regions grid */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {isLoading
          ? [0, 1, 2, 3].map((i) => <Skel key={i} height="96px" />)
          : regions.map((r) => (
              <RegionCard
                key={r.id}
                region={r}
                onClick={() => void navigate({ to: "/regions/$slug", params: { slug: r.slug } })}
              />
            ))}
      </div>
    </div>
  );
}
